/**
 * useTutorLiveFeedSamSummary — la feuille des résumés du champion avec un élève (`TutorLiveOverlays.samSummary`).
 *
 * Port de `common/liveMode/AgentSummaryModal.js` (registre F-101 à F-112) : à l'ouverture, le compteur du
 * champion de l'élève est lu (les n premières sont « NOUVEAU ») puis remis à 0 (`setAgentNotifCount`) ;
 * l'avis « Utile » / « Pas utile » part une fois (`giveAgentFeedback`), puis la séance se relit
 * (`getLiveSessionV2`), les deux par `apiCall` comme le reste de la salle (ni la boîte réseau legacy hors
 * connexion, ni une tranche d'API laissée pleine) ; les mesures `checkpoint.tutor.summary_modal.open` / `.close` et
 * `checkpoint.tutor.feedback.submit`. Une photo s'ouvre en aperçu, d'où l'on crée une activité (F-109, G-18).
 */
import { useCallback, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { setAgentNotifCount } from "../../../../../../data/redux/actions/liveMode"
import { liveService } from "../../../../../../data/services/liveService"
import { liveServiceV2 } from "../../../../../../data/services/liveServiceV2"
import { type TutorSamSummarySheetProps } from "../../../../../../design-system/templates/TutorSamSummarySheet/TutorSamSummarySheet"
import { apiCall } from "../../../../../../engine/shared/apiCall"
import { track } from "../../../hooks/trackEvent"
import { type UICorrectionFeedback } from "../../../types/studentActivity"
import { toUITutorSamSummaries, type ApiLiveSessionUser } from "../../../../adapters/tutor/Live/tutorLiveFeedAdapter"
import { rawId } from "./tutorLiveContract"

export const SAM_SUMMARY_EVENTS = {
    open: { name: "checkpoint.tutor.summary_modal.open" },
    close: { name: "checkpoint.tutor.summary_modal.close" },
    feedback: { name: "checkpoint.tutor.feedback.submit" },
} as const

type NotifCount = { liveSessionId?: unknown; studentId?: unknown; count?: number }

export type TutorLiveSamSummary = {
    props: TutorSamSummarySheetProps | null
    open: (studentId: string) => void
}

export function useTutorLiveFeedSamSummary(args: {
    liveSessionId: number | null
    users: ApiLiveSessionUser[]
    firstnameOf: (studentId: string) => string
    /** La salle porte `checkpoint-rh` : la feuille n'existe que là (`TutorRoomView`, F-101). */
    enabled: boolean
    onOpenPhoto: (studentId: string, uri: string) => void
}): TutorLiveSamSummary {
    const { liveSessionId, users, firstnameOf, enabled, onOpenPhoto } = args
    const dispatch = useDispatch()
    const [openFor, setOpenFor] = useState<{ studentId: string; newCount: number } | null>(null)
    const [given, setGiven] = useState<Record<string, number>>({})
    const counts = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => (state?.liveMode?.agentNotifCounts ?? []) as NotifCount[]
    )
    const countsRef = useRef(counts)
    countsRef.current = counts

    const open = useCallback(
        (studentId: string) => {
            if (!enabled) return
            const notif = countsRef.current.find(
                (entry) => String(entry.studentId) === studentId && entry.liveSessionId === liveSessionId
            )
            setOpenFor({ studentId, newCount: notif?.count ?? 0 })
            setGiven({})
            dispatch(setAgentNotifCount(liveSessionId, rawId(studentId), 0) as never)
            track(SAM_SUMMARY_EVENTS.open)
        },
        [dispatch, enabled, liveSessionId]
    )

    const onFeedback = useCallback(
        (summaryId: string, feedback: UICorrectionFeedback) => {
            if (given[summaryId] !== undefined) return
            const value = feedback === "positive" ? 1 : -1
            track(SAM_SUMMARY_EVENTS.feedback)
            // L'avis reçu relit la séance (`useApiStatus(giveAgentFeedback)` de la modale) ; un échec ne relit rien.
            apiCall(dispatch as never, liveService.giveAgentFeedback, {
                routeParams: { id: rawId(summaryId) },
                data: { feedback_type: "tutor", feedback_value: value },
            })
                .then(() =>
                    apiCall(dispatch as never, liveServiceV2.getLiveSessionV2, { routeParams: { id: liveSessionId } })
                )
                .catch(() => undefined)
            setGiven((previous) => ({ ...previous, [summaryId]: value }))
        },
        [dispatch, given, liveSessionId]
    )

    const studentId = openFor?.studentId
    const conversations = useMemo(
        () => users.find((user) => String(user.id) === studentId)?.conversations ?? [],
        [users, studentId]
    )
    const props = useMemo<TutorSamSummarySheetProps | null>(() => {
        if (openFor == null) return null
        return {
            visible: true,
            onClose: () => {
                track(SAM_SUMMARY_EVENTS.close)
                setOpenFor(null)
            },
            studentName: firstnameOf(openFor.studentId),
            summaries: toUITutorSamSummaries(conversations, { newCount: openFor.newCount, given }),
            onOpenPhoto: (_summaryId, uri) => onOpenPhoto(openFor.studentId, uri),
            onFeedback,
        }
    }, [openFor, conversations, given, firstnameOf, onOpenPhoto, onFeedback])

    return { props, open }
}
